"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import {
  Bot,
  FileUp,
  MessageSquare,
  Code2,
  UserCheck,
  BarChart3,
  ChevronDown,
  Upload,
  FileText,
  Link,
  Copy,
  Check,
  Star,
  Clock,
  SmilePlus,
} from "lucide-react";

interface Scene {
  title: string;
  description: string;
  icon: React.ReactNode;
  visual: React.ReactNode;
}

function SceneCreateAgent() {
  return (
    <div className="space-y-4 w-full max-w-sm mx-auto">
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-gray-400 mb-1.5 text-left">
            Agent Name
          </label>
          <div className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-sm text-white">
            Customer Support Pro
          </div>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1.5 text-left">
            Industry
          </label>
          <div className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-sm text-white flex items-center justify-between">
            <span>Real Estate</span>
            <ChevronDown className="w-4 h-4 text-gray-500" />
          </div>
        </div>
      </div>
      <button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium text-sm py-2.5 rounded-lg hover:opacity-90 transition">
        Create Agent →
      </button>
    </div>
  );
}

function SceneKnowledgeBase() {
  return (
    <div className="space-y-4 w-full max-w-sm mx-auto">
      <div className="border-2 border-dashed border-gray-700 rounded-xl p-6 text-center">
        <Upload className="w-8 h-8 text-gray-500 mx-auto mb-2" />
        <p className="text-sm text-gray-400">
          Drop files here or click to upload
        </p>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-3 bg-gray-800/60 rounded-lg px-3 py-2">
          <FileText className="w-4 h-4 text-blue-400 shrink-0" />
          <span className="text-sm text-gray-300 truncate">company-faq.pdf</span>
          <Check className="w-4 h-4 text-green-400 ml-auto shrink-0" />
        </div>
        <div className="flex items-center gap-3 bg-gray-800/60 rounded-lg px-3 py-2">
          <Link className="w-4 h-4 text-purple-400 shrink-0" />
          <span className="text-sm text-gray-300 truncate">
            https://mysite.com/about
          </span>
          <Check className="w-4 h-4 text-green-400 ml-auto shrink-0" />
        </div>
        <div className="flex items-center gap-3 bg-gray-800/60 rounded-lg px-3 py-2">
          <FileText className="w-4 h-4 text-orange-400 shrink-0" />
          <span className="text-sm text-gray-300 truncate">
            pricing-guide.docx
          </span>
          <div className="ml-auto shrink-0 w-16">
            <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full w-3/4 animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScenePlayground() {
  return (
    <div className="space-y-3 w-full max-w-sm mx-auto">
      <div className="flex justify-end">
        <div className="bg-blue-600 text-white text-sm px-4 py-2 rounded-2xl rounded-br-sm max-w-[75%]">
          Hi, do you have 3-bed flats available in Manchester?
        </div>
      </div>
      <div className="flex justify-start">
        <div className="bg-gray-800 border border-gray-700 text-gray-200 text-sm px-4 py-2 rounded-2xl rounded-bl-sm max-w-[80%]">
          Yes! We have 4 listings in Manchester starting from £285,000. Would
          you like to schedule a viewing?
        </div>
      </div>
      <div className="flex justify-end">
        <div className="bg-blue-600 text-white text-sm px-4 py-2 rounded-2xl rounded-br-sm">
          Yes please, this Saturday?
        </div>
      </div>
      <div className="flex justify-start">
        <div className="bg-gray-800 border border-gray-700 text-gray-200 text-sm px-4 py-2 rounded-2xl rounded-bl-sm max-w-[80%] flex items-start gap-2">
          <span>
            Saturday works! I have 10 AM and 2 PM available. Which do you
            prefer? I&apos;ll need your name and email to confirm.
          </span>
        </div>
      </div>
    </div>
  );
}

function SceneDeploy() {
  const [copied, setCopied] = useState(false);

  return (
    <div className="w-full max-w-sm mx-auto space-y-3">
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 font-mono text-sm relative">
        <button
          onClick={() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          className="absolute top-3 right-3 p-1.5 bg-gray-700 hover:bg-gray-600 rounded-md transition"
          aria-label="Copy code"
        >
          {copied ? (
            <Check className="w-3.5 h-3.5 text-green-400" />
          ) : (
            <Copy className="w-3.5 h-3.5 text-gray-400" />
          )}
        </button>
        <div>
          <span className="text-gray-500">&lt;</span>
          <span className="text-blue-400">script</span>
          <span className="text-purple-400"> src</span>
          <span className="text-gray-500">=</span>
          <span className="text-green-400">&quot;https://cdn.adityalabs.ai/widget.js&quot;</span>
          <span className="text-gray-500"> /&gt;</span>
        </div>
      </div>
      <div className="flex items-center justify-center gap-4 pt-2">
        {["WordPress", "Shopify", "Wix", "Custom"].map((platform) => (
          <span
            key={platform}
            className="text-xs text-gray-500 bg-gray-800/50 px-2.5 py-1 rounded-full border border-gray-800"
          >
            {platform}
          </span>
        ))}
      </div>
    </div>
  );
}

function SceneLeadCapture() {
  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
            New Lead
          </span>
          <span className="bg-green-500/20 text-green-400 text-xs font-semibold px-2.5 py-0.5 rounded-full flex items-center gap-1">
            <Star className="w-3 h-3" />
            Score: 92
          </span>
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
              JD
            </div>
            <div>
              <p className="text-sm font-medium text-white">James Davidson</p>
              <p className="text-xs text-gray-400">james@company.co.uk</p>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-700 pt-3 space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Source</span>
            <span className="text-gray-300">Website Chat</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Interest</span>
            <span className="text-gray-300">3-bed flat, Manchester</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Status</span>
            <span className="text-blue-400 font-medium">Viewing Booked</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SceneAnalytics() {
  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 text-center">
          <MessageSquare className="w-5 h-5 text-blue-400 mx-auto mb-2" />
          <p className="text-xl font-bold text-white">2,847</p>
          <p className="text-xs text-gray-500 mt-0.5">Conversations</p>
        </div>
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 text-center">
          <SmilePlus className="w-5 h-5 text-green-400 mx-auto mb-2" />
          <p className="text-xl font-bold text-white">94%</p>
          <p className="text-xs text-gray-500 mt-0.5">Satisfaction</p>
        </div>
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 text-center">
          <Clock className="w-5 h-5 text-purple-400 mx-auto mb-2" />
          <p className="text-xl font-bold text-white">&lt;3s</p>
          <p className="text-xs text-gray-500 mt-0.5">Avg Response</p>
        </div>
      </div>
      <div className="mt-3 bg-gray-800 border border-gray-700 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-gray-400 font-medium">
            Weekly Trend
          </span>
          <span className="text-xs text-green-400 font-medium">+23%</span>
        </div>
        <div className="flex items-end gap-1 h-12">
          {[35, 45, 38, 52, 48, 65, 72].map((h, i) => (
            <div
              key={i}
              className="flex-1 bg-gradient-to-t from-blue-600/80 to-purple-500/80 rounded-sm"
              style={{ height: `${h}%` }}
            />
          ))}
        </div>
        <div className="flex justify-between mt-1.5">
          {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
            <span key={i} className="text-[10px] text-gray-600 flex-1 text-center">
              {d}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

const scenes: Scene[] = [
  {
    title: "Create Your Agent in Minutes",
    description:
      "Choose an industry template or start from scratch. No coding required.",
    icon: <Bot className="w-5 h-5" />,
    visual: <SceneCreateAgent />,
  },
  {
    title: "Train with Your Knowledge Base",
    description:
      "Upload FAQs, documents, and URLs. Your agent learns your business.",
    icon: <FileUp className="w-5 h-5" />,
    visual: <SceneKnowledgeBase />,
  },
  {
    title: "Test in the Playground",
    description: "Preview real AI conversations before going live.",
    icon: <MessageSquare className="w-5 h-5" />,
    visual: <ScenePlayground />,
  },
  {
    title: "Deploy Across Reviewed Channels",
    description:
      "One line of code. Supports common platforms — Shopify, WordPress, or Wix. Additional host validation pending.",
    icon: <Code2 className="w-5 h-5" />,
    visual: <SceneDeploy />,
  },
  {
    title: "Capture Leads Automatically",
    description:
      "Every conversation becomes a qualified lead in your CRM.",
    icon: <UserCheck className="w-5 h-5" />,
    visual: <SceneLeadCapture />,
  },
  {
    title: "Track Performance",
    description:
      "Real-time analytics on conversations, satisfaction, and ROI.",
    icon: <BarChart3 className="w-5 h-5" />,
    visual: <SceneAnalytics />,
  },
];

const SCENE_DURATION = 5000; // 5 seconds per scene
const PROGRESS_TICK = 20; // update progress every 20ms for smooth bar

export default function ProductTour() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [direction, setDirection] = useState<"next" | "prev">("next");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isInView, setIsInView] = useState(false);

  const isPausedRef = useRef(isPaused);
  const isInViewRef = useRef(false);
  const progressRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  // Keep refs in sync with state
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    isInViewRef.current = isInView;
  }, [isInView]);

  // IntersectionObserver — reset to scene 0 on entry, track visibility
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const nowVisible = entry.isIntersecting;
        setIsInView(nowVisible);
        if (nowVisible) {
          setActiveIndex(0);
          setDirection("next");
          setIsTransitioning(false);
          progressRef.current = 0;
          setProgress(0);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Navigate to a specific scene and reset the progress timer
  const goToScene = useCallback(
    (index: number) => {
      if (index === activeIndex || isTransitioning) return;
      setDirection(index > activeIndex ? "next" : "prev");
      setIsTransitioning(true);
      // Reset progress for the new scene
      progressRef.current = 0;
      setProgress(0);
      setTimeout(() => {
        setActiveIndex(index);
        setTimeout(() => setIsTransitioning(false), 50);
      }, 200);
    },
    [activeIndex, isTransitioning]
  );

  // Single interval drives both the progress bar and auto-advance
  // Only runs when the component is in the viewport
  useEffect(() => {
    // Reset the progress counter for the new scene. The visible bar is already
    // reset to 0 by every entry path (IntersectionObserver on enter, goToScene,
    // and auto-advance) and is then driven up by the interval below, so we avoid
    // a synchronous setState in the effect body (cascading renders).
    progressRef.current = 0;

    if (!isInView) return;

    timerRef.current = setInterval(() => {
      if (isPausedRef.current || !isInViewRef.current) return;

      progressRef.current += PROGRESS_TICK;
      const pct = Math.min((progressRef.current / SCENE_DURATION) * 100, 100);
      setProgress(pct);

      if (progressRef.current >= SCENE_DURATION) {
        progressRef.current = 0;
        setProgress(0);
        setDirection("next");
        setIsTransitioning(true);
        setTimeout(() => {
          setActiveIndex((prev) => (prev + 1) % scenes.length);
          setTimeout(() => setIsTransitioning(false), 50);
        }, 200);
      }
    }, PROGRESS_TICK);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [activeIndex, isInView]);

  const scene = scenes[activeIndex];

  return (
    <section ref={sectionRef} className="py-20 px-6 bg-gray-950">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-blue-400 mb-3">
            Product Tour
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            From Zero to Live in{" "}
            <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              5 Minutes
            </span>
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto">
            See how Aditya Labs takes you from idea to a fully deployed AI agent,
            step by step.
          </p>
        </div>

        {/* Tour Card */}
        <div
          className="relative bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden"
        >
          {/* Per-scene progress bar — fills over 5s, resets each scene */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gray-800 z-10">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
              style={{
                width: `${progress}%`,
                transition: isPaused
                  ? "none"
                  : `width ${PROGRESS_TICK}ms linear`,
              }}
            />
          </div>

          <div className="grid md:grid-cols-5 min-h-[420px]">
            {/* Left sidebar — scene list */}
            <div className="md:col-span-2 border-b md:border-b-0 md:border-r border-gray-800 p-6">
              <div className="space-y-1">
                {scenes.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => goToScene(i)}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-300 group flex items-start gap-3 ${
                      i === activeIndex
                        ? "bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30"
                        : "hover:bg-gray-800/50 border border-transparent"
                    }`}
                  >
                    <span
                      className={`mt-0.5 shrink-0 ${
                        i === activeIndex
                          ? "text-blue-400"
                          : "text-gray-600 group-hover:text-gray-400"
                      } transition-colors`}
                    >
                      {s.icon}
                    </span>
                    <div>
                      <p
                        className={`text-sm font-medium transition-colors ${
                          i === activeIndex
                            ? "text-white"
                            : "text-gray-500 group-hover:text-gray-300"
                        }`}
                      >
                        {s.title}
                      </p>
                      {i === activeIndex && (
                        <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                          {s.description}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Right — visual mockup */}
            <div className="md:col-span-3 flex items-center justify-center p-8 md:p-12 relative overflow-hidden">
              {/* Background glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 via-transparent to-purple-600/5 pointer-events-none" />

              <div
                className={`w-full transition-all duration-300 ease-out ${
                  isTransitioning
                    ? `opacity-0 ${
                        direction === "next"
                          ? "translate-y-4"
                          : "-translate-y-4"
                      }`
                    : "opacity-100 translate-y-0"
                }`}
              >
                {/* Step label */}
                <div className="text-center mb-6 md:hidden">
                  <h3 className="text-lg font-bold text-white mb-1">
                    {scene.title}
                  </h3>
                  <p className="text-sm text-gray-400">{scene.description}</p>
                </div>

                {scene.visual}
              </div>
            </div>
          </div>

          {/* Dot navigation */}
          <div className="flex items-center justify-center gap-2 pb-5">
            {scenes.map((_, i) => (
              <button
                key={i}
                onClick={() => goToScene(i)}
                aria-label={`Go to step ${i + 1}`}
                className={`rounded-full transition-all duration-300 ${
                  i === activeIndex
                    ? "w-6 h-2 bg-gradient-to-r from-blue-500 to-purple-500"
                    : "w-2 h-2 bg-gray-700 hover:bg-gray-600"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Pause hint */}
        <p className="text-center text-xs text-gray-600 mt-4">
          Hover to pause &middot; Click any step to jump ahead
        </p>
      </div>
    </section>
  );
}
